import React, { memo, useCallback, useMemo } from "react";
import { Button, HStack, Spacer, Stack, Text, VStack } from "@chakra-ui/react";
import colors from "app/colors";

import {
  DistributionGroupFragment,
  UserFragment,
  UserRoleType,
} from "gql/graphql";
import { ClockArrowPlusIcon, UserEmailCircleIcon } from "modules/icons";
import { GroupedVirtuoso } from "react-virtuoso";
import { useToggle } from "react-use";
import UserAvatar from "modules/_shared/user-avatar";
import UserName from "modules/_shared/user-name";
import { getUserFullName, isUserInactive } from "modules/utils/Functions";
import AdminToggleNotificationsDistributionGroupButton from "../buttons/admin-toggle-notifications-distribution-group-button";
import AdminGrantHistoricalAccessButton from "../buttons/admin-grant-historical-access-button";

interface IProps {
  customScrollParent?: React.RefObject<HTMLDivElement>;
  distributionGroup?: DistributionGroupFragment;
  users: UserFragment[];
  emailUsers: UserFragment[];
}

const DISPLAY_USER_COUNT = 5;

const AdminDistributionGroupMemberList = ({
  customScrollParent,
  distributionGroup,
  users,
  emailUsers,
}: IProps) => {
  const [limitUsers, toggleLimitUsers] = useToggle(true);

  const limitedUserList = useMemo(() => {
    if (!limitUsers) return users;
    return users.slice(0, DISPLAY_USER_COUNT);
  }, [limitUsers, users]);

  const groupCounts = useMemo(
    () => [limitedUserList.length, emailUsers.length || 0],
    [emailUsers.length, limitedUserList.length],
  );

  const hasUsers = limitedUserList.length > 0 || emailUsers.length > 0;

  const userCountWithoutHistoricalAccess = distributionGroup
    ? distributionGroup.userIdList.length -
      distributionGroup.historicalAccessUserIdList.length
    : 0;

  //prevents the section header from showing when there are no users in that section
  // e.g. if there are no email users, the "External Users" header should not be visible
  // prevents errors in GroupedVirtuoso when there are no items in a section
  const isSectionHeaderVisible = useCallback(
    (sectionIndex: number) => {
      if (sectionIndex === 0) {
        return limitedUserList.length > 0;
      }
      return emailUsers.length > 0;
    },
    [emailUsers.length, limitedUserList.length],
  );

  const renderGroupHeader = useCallback(
    (sectionIndex: number) => {
      if (sectionIndex === 0) {
        return (
          <VStack w="full" alignItems="flex-start" spacing={1}>
            <Text
              color={colors.blackSecondary}
              w="full"
              borderBottomWidth={1}
              borderColor={colors.grayPrimary}
              fontSize={16}
              fontWeight={700}
              bg={colors.white}
              visibility={
                isSectionHeaderVisible(sectionIndex) ? "visible" : "hidden"
              }
            >
              {`Users (${users.length})`}
            </Text>
            {userCountWithoutHistoricalAccess > 0 && (
              <HStack w="full" spacing={1}>
                <ClockArrowPlusIcon boxSize={4} fill={colors.grayMain} />
                <Text fontSize={12} fontWeight={500} color={colors.grayMain}>
                  {userCountWithoutHistoricalAccess} User
                  {userCountWithoutHistoricalAccess > 1 ? "s" : ""} without
                  Historical Access
                </Text>
              </HStack>
            )}
          </VStack>
        );
      }
      return (
        <Text
          w="full"
          borderBottomWidth={1}
          borderColor={colors.grayPrimary}
          color={colors.blackSecondary}
          fontSize={16}
          fontWeight={700}
          bg={colors.white}
          pt={4}
          visibility={
            isSectionHeaderVisible(sectionIndex) ? "visible" : "hidden"
          }
        >
          {`External Users (${emailUsers.length})`}
        </Text>
      );
    },
    [
      emailUsers.length,
      isSectionHeaderVisible,
      userCountWithoutHistoricalAccess,
      users.length,
    ],
  );

  const renderItem = useCallback(
    (index: number, groupindex: number) => {
      if (groupindex === 0) {
        return (
          <>
            <UserItem
              user={limitedUserList[index]}
              distributionGroup={distributionGroup}
            />
            {users.length > DISPLAY_USER_COUNT &&
              index === limitedUserList.length - 1 && (
                <Button
                  variant="link"
                  size="xs"
                  color={colors.blue}
                  fontWeight={700}
                  fontSize={12}
                  onClick={toggleLimitUsers}
                >
                  {limitUsers ? "+ Show More" : "- Show Less"}
                </Button>
              )}
          </>
        );
      }
      const emailUser = emailUsers[index - limitedUserList.length];
      return (
        <UserItem user={emailUser} distributionGroup={distributionGroup} />
      );
    },
    [
      emailUsers,
      limitedUserList,
      distributionGroup,
      users.length,
      toggleLimitUsers,
      limitUsers,
    ],
  );

  if (!hasUsers) {
    return null;
  }

  return (
    <GroupedVirtuoso
      customScrollParent={customScrollParent?.current ?? undefined}
      groupCounts={groupCounts}
      groupContent={renderGroupHeader}
      itemContent={renderItem}
      style={{ width: "100%", height: "100%", marginTop: 8 }}
    />
  );
};

export default memo(AdminDistributionGroupMemberList);

interface IUserItemProps {
  distributionGroup?: DistributionGroupFragment;
  user: UserFragment;
}

const UserItem: React.FC<IUserItemProps> = ({ distributionGroup, user }) => {
  if (UserRoleType.Email === user.roleType) {
    return (
      <HStack w="full" py={1} borderBottomWidth={1} borderColor={colors.gray0}>
        <UserEmailCircleIcon boxSize={6} fill={colors.white} />
        <Stack spacing={0}>
          {getUserFullName(user) && <Text>{getUserFullName(user)}</Text>}
          <Text color={colors.gray50}>{user.email}</Text>
        </Stack>
      </HStack>
    );
  }

  return (
    <HStack w="full" py={1} borderBottomWidth={1} borderColor={colors.gray0}>
      <UserAvatar userId={user.id} size="xs" displayOnlineIndicator />
      <Text>
        <UserName userId={user.id} />
      </Text>
      <Text fontSize={12} color={colors.grayMain}>
        {isUserInactive(user) && "(Inactive)"}
      </Text>
      <Spacer />
      <AdminGrantHistoricalAccessButton
        distributionGroup={distributionGroup}
        userId={user.id}
      />
      <AdminToggleNotificationsDistributionGroupButton
        distributionGroup={distributionGroup}
        userId={user.id}
      />
    </HStack>
  );
};
